<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AnHeader from "./AnHeader.vue";
import AnDonut from "./AnDonut.vue";
import AnAreaLine from "./AnAreaLine.vue";
import AnRankBars from "./AnRankBars.vue";
import AnalyticsService from "@/services/analytics";
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();
const COLORS = { good: "#34d399", bad: "#fb7185", accent2: "#8b7cf6" };

// ── state ────────────────────────────────────────────────────────
const period = ref("daily");
const dateRange = ref(null);
const loading = ref(false);

const conversion = ref({ checkout: 0, timeout: 0, total: 0, rate: 0, open: 0 });
const conversionSegments = computed(() => [
  { label: "Checkout handoff", value: conversion.value.checkout, color: COLORS.good },
  { label: "Timeout", value: conversion.value.timeout, color: COLORS.bad },
]);
const centerTop = computed(() => (conversion.value.rate * 100).toFixed(1) + "%");

const cartTimelineSeries = ref([]);
const productMovements = ref([]);
const abandonmentRows = ref([]);
const coOccurrence = ref([]);

// ── helpers ──────────────────────────────────────────────────────
function buildParams() {
  const p = { period: period.value };
  if (dateRange.value?.[0] && dateRange.value?.[1]) {
    p.from_date = dateRange.value[0].toISOString();
    p.until_date = dateRange.value[1].toISOString();
  }
  return p;
}

const ratioColor = (r) => (r > 0.5 ? COLORS.bad : r > 0.3 ? "#fbbf24" : COLORS.good);
const ratioLabel = (r) => `${(r * 100).toFixed(0)}%`;

// ── fetch ────────────────────────────────────────────────────────
async function fetchData() {
  const companyId = companyStore.companyId;
  if (!companyId) return;
  loading.value = true;
  try {
    const params = buildParams();
    const [convRes, timelineRes, movRes, abandRes, coocRes] = await Promise.all([
      AnalyticsService.getCartConversion(companyId, params),
      AnalyticsService.getCartTimeline(companyId, params),
      AnalyticsService.getCartProductMovements(companyId, params),
      AnalyticsService.getCartProductAbandonment(companyId, params),
      AnalyticsService.getCartCoOccurrence(companyId, params),
    ]);

    // Conversion
    if (convRes) {
      conversion.value = {
        checkout: convRes.checkout ?? convRes.checkout_count ?? 0,
        timeout: convRes.timeout ?? convRes.timeout_count ?? 0,
        open: convRes.open ?? 0,
        total: convRes.total ?? convRes.total_sessions ?? 0,
        rate: convRes.conversion_rate ?? convRes.rate ?? 0,
      };
    }

    // Timeline
    if (timelineRes?.data?.length) {
      const sorted = [...timelineRes.data].sort((a, b) => new Date(a.date || a.period_date) - new Date(b.date || b.period_date));
      const added = sorted.map((d) => d.units_added ?? 0);
      const removed = sorted.map((d) => d.units_removed ?? 0);
      const net = sorted.map((d) => (d.net_units ?? (d.units_added ?? 0) - (d.units_removed ?? 0)));
      cartTimelineSeries.value = [
        { values: added, color: COLORS.accent2, fill: "rgba(139,124,246,.14)" },
        { values: removed, color: COLORS.bad, fill: null, dash: true, width: 1.8 },
        { values: net, color: COLORS.good, fill: null, width: 2 },
      ];
    }

    // Product movements
    if (movRes?.items?.length) {
      productMovements.value = movRes.items.slice(0, 6).map((p) => ({
        name: p.product_title || p.name || "—",
        brand: p.brand || "",
        added: p.units_added ?? p.added ?? 0,
        removed: p.units_removed ?? p.removed ?? 0,
        net: p.net_units ?? p.net ?? 0,
        ratio: p.removal_ratio ?? p.ratio ?? 0,
      }));
    }

    // Abandonment
    if (abandRes?.items?.length) {
      abandonmentRows.value = abandRes.items.slice(0, 5).map((p) => {
        const rate = p.abandonment_rate ?? p.rate ?? 0;
        return {
          name: p.product_title || p.name || "—",
          sub: `${p.abandoned_count ?? p.abandoned ?? 0} abandoned · ${(rate * 100).toFixed(0)}% rate`,
          value: p.abandoned_count ?? p.abandoned ?? 0,
          color: rate > 0.5 ? COLORS.bad : "#fbbf24",
        };
      });
    }

    // Co-occurrence
    if (coocRes?.pairs?.length) {
      coOccurrence.value = coocRes.pairs.slice(0, 5).map((p) => ({
        productA: p.product_a_title || p.productA || "—",
        productB: p.product_b_title || p.productB || "—",
        count: p.co_occurrence_count ?? p.count ?? 0,
      }));
    }
  } catch (e) {
    console.error("CartBoard fetch error:", e);
  } finally {
    loading.value = false;
  }
}

watch([period, dateRange], fetchData);
onMounted(fetchData);
</script>

<template>
  <div class="an-page">
    <AnHeader
      title="Cart Activity"
      subtitle="Recorded checkout handoffs, product movements, abandonment risk, and basket co-occurrence. A handoff is not a confirmed sale."
      @period-change="p => { period = p.toLowerCase(); }"
      @date-change="dr => { dateRange = dr; }"
    />

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="an-grid">
        <div class="an-panel an-c4"><div class="an-skel an-skel--chart"><Skeletor height="14" width="45%" as="div" /><Skeletor height="130" width="130" as="div" style="border-radius:50%;margin:8px auto" /><Skeletor height="10" width="60%" as="div" style="margin:0 auto" /></div></div>
        <div class="an-panel an-c8"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="160" as="div" /></div></div>
        <div class="an-panel an-c5"><div class="an-skel"><Skeletor height="14" width="45%" as="div" /><Skeletor v-for="j in 5" :key="j" height="14" as="div" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 4" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c3"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 4" :key="j" height="12" as="div" /></div></div>
      </div>
    </template>

    <div v-else class="an-grid">
      <!-- Conversion -->
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Checkout Handoff Rate</div></div>
        <AnDonut
          v-if="conversion.total > 0"
          :segments="conversionSegments"
          :center-top="centerTop"
          center-sub="checkout handoff"
        />
        <div v-else class="an-empty-panel">No conversion data</div>
        <div class="cart-conv-row" v-if="conversion.total > 0">
          <div><b style="color:var(--an-good)">{{ conversion.checkout.toLocaleString() }}</b><span>checkout handoff</span></div>
          <div><b style="color:var(--an-bad)">{{ conversion.timeout.toLocaleString() }}</b><span>timed out</span></div>
          <div><b>{{ conversion.total.toLocaleString() }}</b><span>total visits</span></div>
        </div>
      </div>

      <!-- Cart timeline -->
      <div class="an-panel an-c8">
        <div class="an-panel__head"><div class="an-panel__title">Cart Activity Timeline</div></div>
        <AnAreaLine v-if="cartTimelineSeries.length" :series="cartTimelineSeries" :height="160" />
        <div v-else class="an-empty-panel">No timeline data</div>
        <div class="an-legend-row" v-if="cartTimelineSeries.length">
          <span><i style="background:var(--an-accent-2)"></i>Units added</span>
          <span><i style="background:var(--an-bad)"></i>Removed</span>
          <span><i style="background:var(--an-good)"></i>Net</span>
        </div>
      </div>

      <!-- Product movements -->
      <div class="an-panel an-c5">
        <div class="an-panel__head"><div class="an-panel__title">Product Movements</div></div>
        <div v-if="productMovements.length" class="cart-mov-table">
          <div class="cart-mov-head">
            <span>Product</span><span>Added</span><span>Removed</span><span>Regret</span>
          </div>
          <div v-for="(p, i) in productMovements" :key="i" class="cart-mov-row">
            <div class="cart-mov-name">
              <span>{{ p.name }}</span>
              <small>{{ p.brand }}</small>
            </div>
            <div class="cart-mov-num cart-num-add">+{{ p.added }}</div>
            <div class="cart-mov-num cart-num-rem">−{{ p.removed }}</div>
            <div class="cart-mov-badge" :style="{ background: `${ratioColor(p.ratio)}22`, color: ratioColor(p.ratio) }">
              {{ ratioLabel(p.ratio) }}
            </div>
          </div>
        </div>
        <div v-else class="an-empty-panel">No movement data</div>
      </div>

      <!-- Abandonment -->
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Abandonment Risk</div></div>
        <AnRankBars v-if="abandonmentRows.length" :rows="abandonmentRows" />
        <div v-else class="an-empty-panel">No abandonment data</div>
      </div>

      <!-- Co-occurrence -->
      <div class="an-panel an-c3">
        <div class="an-panel__head"><div class="an-panel__title">Bought Together</div></div>
        <div v-if="coOccurrence.length" class="cart-cooc">
          <div v-for="(p, i) in coOccurrence" :key="i" class="cart-cooc-row">
            <div class="cart-cooc-pair">
              <span>{{ p.productA }}</span>
              <span class="cart-cooc-sep">+</span>
              <span>{{ p.productB }}</span>
            </div>
            <div class="cart-cooc-count">{{ p.count }}×</div>
          </div>
        </div>
        <div v-else class="an-empty-panel">No co-occurrence data</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.an-empty-panel { color: var(--an-muted); font-size: 12px; padding: 16px 0; }

.cart-conv-row {
  display: flex; justify-content: space-around; margin-top: 12px;
  padding-top: 12px; border-top: 1px solid var(--an-line); text-align: center;
}
.cart-conv-row div { display: flex; flex-direction: column; gap: 3px; }
.cart-conv-row b { font-size: 18px; font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; }
.cart-conv-row span { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--an-muted); }

.cart-mov-table { display: flex; flex-direction: column; gap: 0; }
.cart-mov-head {
  display: grid; grid-template-columns: 1fr 52px 62px 46px;
  gap: 0 8px; padding: 0 0 6px;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: var(--an-muted);
  border-bottom: 1px solid var(--an-line); margin-bottom: 4px;
}
.cart-mov-head span:not(:first-child) { text-align: right; }
.cart-mov-row {
  display: grid; grid-template-columns: 1fr 52px 62px 46px;
  gap: 0 8px; padding: 7px 0; border-bottom: 1px solid var(--an-line); align-items: center;
}
.cart-mov-row:last-child { border: none; }
.cart-mov-name { min-width: 0; }
.cart-mov-name span { display: block; font-size: 12px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cart-mov-name small { font-size: 10.5px; color: var(--an-muted); }
.cart-mov-num { font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; text-align: right; }
.cart-num-add { color: var(--an-good); }
.cart-num-rem { color: var(--an-bad); }
.cart-mov-badge {
  font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums;
  text-align: right; padding: 2px 6px; border-radius: 4px; justify-self: end;
}

.cart-cooc { display: flex; flex-direction: column; gap: 0; }
.cart-cooc-row {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid var(--an-line);
}
.cart-cooc-row:last-child { border: none; }
.cart-cooc-pair { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.cart-cooc-pair span { font-size: 11.5px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--an-text); }
.cart-cooc-sep { font-size: 10px; color: var(--an-muted) !important; font-weight: 400 !important; }
.cart-cooc-count { font-size: 14px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--an-accent-2); flex: none; }
</style>
